import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { MetaTag } from '@models/common.model';

@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
    private readonly metaKeyMap: Record<string, string[]> = {
        title: ['og:title', 'twitter:title'],
        description: ['og:description', 'twitter:description'],
    };

    constructor(
        private readonly _title: Title,
        private readonly _meta: Meta
    ) {
        super();
    }

    /**
     * Update the title and meta tags based on the router state
     *
     * @memberof PageTitleStrategy
     */
    override updateTitle(routerState: RouterStateSnapshot) {
        const metaTag = this._getDeepestData(routerState.root);
        const title = this.buildTitle(routerState) || metaTag?.title;
        if (title) {
            this._meta.removeTag(`name="title"`);
            this._title.setTitle(title);
        }
        if (metaTag) {
            this._updateMetaTags(metaTag);
        }
    }

    /**
     * Get the title from the router state
     *
     * @private
     * @param {ActivatedRouteSnapshot} route
     * @return {*}  {(MetaTag | undefined)}
     * @memberof PageTitleStrategy
     */
    private _getDeepestData(route: ActivatedRouteSnapshot): MetaTag | undefined {
        let lastMetaTag: MetaTag | undefined;

        while (route) {
            if (route.data['metaTags']) {
                lastMetaTag = route.data['metaTags'];
            }
            route = route.firstChild!;
        }

        return lastMetaTag;
    }

    /**
     * Build the title from the router state
     *
     * @private
     * @param {MetaTag} metaTag
     * @memberof PageTitleStrategy
     */
    private _updateMetaTags(metaTag: MetaTag) {
        Object.keys(metaTag).forEach((key) => {
            const k = key as keyof MetaTag;
            const content = metaTag[k];
            if (k in metaTag && content) {
                this._meta.updateTag({ name: key, content: content });
                const metaKeys = this.metaKeyMap[k];
                metaKeys?.forEach((metaKey) => {
                    this._meta.updateTag({ name: metaKey, content });
                });
            }
        });
    }
}
